import { CELL_PX, GRID, TILE_PX } from './screenWatermark';

export type ScreenMarkAppearance = 'dots' | 'horizontal';
export type ScreenMarkGround = 'onDark' | 'onLight';

/** Same v2 payload and 64px repetition in both modes. Only the optical carrier changes. */
export function paintScreenMarkTile(
  cells: boolean[], ground: ScreenMarkGround, appearance: ScreenMarkAppearance, density: number,
): string | null {
  const canvas = document.createElement('canvas');
  const scale = Math.max(1, Math.min(4, Math.round(density || 1)));
  canvas.width = canvas.height = TILE_PX * scale;
  const context = canvas.getContext('2d');
  if (!context) return null;

  const light = ground === 'onLight';
  const color = light ? '15, 23, 42' : '255, 255, 255';
  if (appearance === 'dots') {
    // Preserve the legacy painter exactly: 4px square within each 8px cell.
    const cell = CELL_PX * scale;
    const dot = Math.max(1, Math.round(cell * 0.5));
    const inset = Math.floor((cell - dot) / 2);
    context.fillStyle = `rgba(${color}, ${light ? 0.020 : 0.018})`;
    cells.forEach((on, index) => {
      if (on) context.fillRect((index % GRID) * cell + inset, Math.floor(index / GRID) * cell + inset, dot, dot);
    });
  } else {
    // The selected horizontal preview's peak strength. Moving some constant ink into
    // the data swing improves recovery while making every pixel no brighter than
    // the preview. Every row stays continuous, without isolated square dots.
    // Keep this formula aligned with the real-browser recovery benchmark.
    const pixels = context.createImageData(canvas.width, canvas.height);
    const [red, green, blue] = light ? [15, 23, 42] : [255, 255, 255];
    const delta = light ? 0.053 : 0.048;
    for (let y = 0; y < canvas.height; y += 1) for (let x = 0; x < canvas.width; x += 1) {
      const along = (x + 0.5) / scale / CELL_PX - 0.5;
      const across = (y + 0.5) / scale / CELL_PX;
      const row = Math.floor(across);
      const column = Math.floor(along);
      const blend = (1 - Math.cos(Math.PI * (along - column))) / 2;
      const left = Number(cells[row * GRID + (column + GRID) % GRID]);
      const right = Number(cells[row * GRID + (column + 1) % GRID]);
      const signal = left * (1 - blend) + right * blend;
      const envelope = Math.sin(Math.PI * (across - row)) ** 2;
      const index = (y * canvas.width + x) * 4;
      pixels.data[index] = red;
      pixels.data[index + 1] = green;
      pixels.data[index + 2] = blue;
      pixels.data[index + 3] = Math.round(255 * 0.4 * (0.025 + delta * signal) * envelope);
    }
    context.putImageData(pixels, 0, 0);
  }
  try { return canvas.toDataURL('image/png'); } catch { return null; }
}
